import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Loader2,
  CheckCircle2,
  Send,
  Link2,
  Share2,
  Wallet,
  Banknote,
  ShieldCheck,
  Truck,
  FlaskConical,
  FileCheck2,
  Clock3,
  ArrowRight,
} from "lucide-react";
import api from "../../lib/api";
import JunkFolderHint from "../JunkFolderHint";

const TRUST = [
  { icon: Truck, label: "Same-day UK dispatch", sub: "Orders before 2pm" },
  { icon: FlaskConical, label: "99%+ purity", sub: "Every batch" },
  { icon: FileCheck2, label: "CoA on every product", sub: "Third-party tested" },
  { icon: Clock3, label: "5-day clearing", sub: "Then cash out any time" },
];

export function TrustStrip() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-10" data-testid="aff-trust-strip">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TRUST.map(({ icon: Icon, label, sub }) => (
          <div key={label} className="flex items-center gap-3 p-3 bg-[#0B1220] border border-[#1E2E44] rounded-sm">
            <Icon size={18} className="text-[#A6FF00] shrink-0" />
            <div>
              <div className="text-[#F7FAFC] text-sm font-semibold leading-tight">{label}</div>
              <div className="text-[#9CA8B8] text-[11px]">{sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STEPS = [
  { icon: Link2, title: "Get your link", body: "Approved partners receive a unique link + code and a live dashboard by email." },
  { icon: Share2, title: "Share it", body: "Lab groups, newsletters, forums, socials, WhatsApp. Ready-made compliant share card included." },
  { icon: Wallet, title: "Pot fills up", body: "10% of every new customer's first order lands in your pot, pending for 5 days, then available." },
  { icon: Banknote, title: "Cash out your way", body: "Instant store credit worth 1.5× (15%) — or a bank transfer from £20, paid 3–5 working days after approval." },
];

export function HowItWorks() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-12" data-testid="aff-how-it-works">
      <div className="pg-micro mb-2">// How it works</div>
      <h3 className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-6" style={{ fontFamily: "'Rajdhani'" }}>
        Four steps. No targets, no tiers, no small print.
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STEPS.map(({ icon: Icon, title, body }, i) => (
          <div key={title} className="pg-card p-5 relative overflow-hidden">
            <div className="absolute -right-2 -top-3 text-[64px] font-black text-[#1E2E44] select-none" style={{ fontFamily: "'Exo 2'" }}>
              {i + 1}
            </div>
            <Icon size={20} className="text-[#00C2D6] mb-3 relative" />
            <div className="text-[#F7FAFC] text-lg pg-heading mb-1 relative" style={{ fontFamily: "'Rajdhani'" }}>{title}</div>
            <div className="text-[#9CA8B8] text-sm leading-relaxed relative">{body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FAQS = [
  ["How much do I actually earn?", "10% of the item price (after any discount, shipping excluded) on every new customer's first paid order. Cash that out as store credit and it's worth 1.5× — so 15% of the order. A £240 first order = £24 in your pot, or £36 of credit."],
  ["What counts as a qualifying order?", "A brand-new customer's first paid order, placed within 30 days of clicking your link. We match customers by email, so nobody gets paid twice for the same person — including you."],
  ["Why the first order only?", "It lets us pay a high rate on the order you genuinely generated, keep prices low for researchers, and keep the rules dead simple. There are no tiers, targets or clawback surprises."],
  ["When can I cash out?", "Each commission is pending for 5 days after the order is paid (our refund and checks window), then it's available. From there you cash out whenever you like — there's no schedule and no minimum for store credit. Bank transfers start at £20."],
  ["How does the bank transfer work?", "You request it from your dashboard, we approve it (usually same or next working day) and it lands in your UK account within 3–5 working days. You'll get an email at each step. Non-UK accounts: IBAN + SWIFT."],
  ["How does store credit work?", "You get a single-use code worth 1.5× your pot, instantly, by email and in your dashboard. Use it in the store-credit box at checkout on anything in the catalogue — it stacks with discount codes. Postage still applies and it never expires; it has no cash value."],
  ["How do you track my referrals?", "Your link sets a 30-day cookie in the visitor's browser (server-side too, so it survives Safari's 7-day clear-outs). Any first order they place in that window is stamped with your code and appears in your dashboard the moment it's paid."],
  ["Do I need a website or big following?", "No. Most partners share in lab groups, Discord/Telegram communities, newsletters, forums, or directly with colleagues. We give you a ready-made, compliant share card for Instagram, X, TikTok and Reddit."],
  ["What can't I say?", "Peptigen Labs products are research-use-only. You must never make medical, therapeutic, cosmetic, performance or human-consumption claims, share dosing or personal-use content, or target under-18s. Every post needs a clear #ad or #affiliate disclosure (UK ASA/CAP). No unsolicited email or DMs (PECR)."],
  ["Can I use my own link for my own orders?", "No. Self-referrals (your email, your address, or clusters of orders to one address) are flagged automatically, held, and voided. It's the fastest way to lose your account."],
  ["Do my referrals get a discount too?", "Yes — new customers get 10% off their first order with WELCOME10, and it's printed on your share card. Your commission is calculated after that discount."],
  ["Why is it invite-only right now?", "We're onboarding a first group of partners so we can look after everyone properly. Apply below — we read every application and reply within a couple of days. Approved partners get their link and dashboard immediately."],
  ["What about tax?", "Commission is paid gross. You're responsible for declaring it to HMRC if required; you can store a UTR or VAT number in your dashboard for your own records."],
];

export function FAQ() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-16" data-testid="aff-faq">
      <div className="pg-micro mb-2">// Questions, answered</div>
      <h3 className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-6" style={{ fontFamily: "'Rajdhani'" }}>
        Everything partners ask before joining.
      </h3>
      <Accordion type="single" collapsible className="pg-card divide-y divide-[#1E2E44]">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`faq-${i}`} className="border-0 px-5" data-testid={`aff-faq-item-${i}`}>
            <AccordionTrigger className="text-left text-[#F7FAFC] text-[15px] hover:no-underline hover:text-[#A6FF00] py-4">
              {q}
            </AccordionTrigger>
            <AccordionContent className="text-[#9CA8B8] text-sm leading-relaxed pb-5">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function ComplianceCard() {
  return (
    <div className="pg-card p-6" data-testid="aff-compliance-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-[#00C2D6]" />
        <div className="text-[#F7FAFC] text-lg pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
          The three rules
        </div>
      </div>
      <ul className="space-y-2 text-[#9CA8B8] text-sm leading-relaxed">
        <li>• Research-use only — no medical, therapeutic or human-consumption claims, ever.</li>
        <li>• Disclose every time — #ad / #affiliate on anything with your link.</li>
        <li>• No spam, no self-referrals, no bidding on “Peptigen” in paid ads.</li>
      </ul>
      <Link
        to="/legal/affiliate-terms"
        className="inline-flex items-center gap-1.5 mt-4 text-[#00C2D6] text-xs pg-mono uppercase tracking-[0.2em] hover:underline"
        data-testid="aff-terms-link"
      >
        Full programme terms <ArrowRight size={12} />
      </Link>
    </div>
  );
}

const INPUT = "w-full bg-[#0B1220] border border-[#1E2E44] text-[#F7FAFC] rounded-sm px-3.5 py-3 text-sm focus:outline-none focus:border-[#00C2D6] transition-colors";

export function RequestInviteForm() {
  const [form, setForm] = useState({ name: "", email: "", website: "", audience: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) return toast.error("Please accept the Affiliate Programme Terms to continue");
    if (!form.name.trim() || !form.email.includes("@")) return toast.error("Please supply your name and a valid email");
    setLoading(true);
    try {
      const { data } = await api.post("/affiliates/apply", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        website: form.website || null,
        audience: form.audience || null,
        accepted_terms: true,
      });
      setDone({ email: form.email.trim().toLowerCase(), message: data.message });
      toast.success(data.message || "Application received");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not send your application — please try again");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="pg-card p-6 sm:p-8" data-testid="aff-apply-done">
        <CheckCircle2 size={28} className="text-[#A6FF00] mb-3" />
        <div className="text-[#F7FAFC] text-2xl pg-heading mb-2" style={{ fontFamily: "'Rajdhani'" }}>
          Application received
        </div>
        <p className="text-[#9CA8B8] text-sm leading-relaxed mb-4">{done.message} We've sent a confirmation to {done.email}.</p>
        <JunkFolderHint email={done.email} what="our confirmation" size="sm" />
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="pg-card p-6 sm:p-8 space-y-5" data-testid="aff-apply-form" id="apply">
      <div>
        <div className="inline-flex items-center gap-2 pg-micro mb-2" style={{ color: "#A6FF00" }}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#A6FF00] animate-pulse" /> Invite-only · first partner group
        </div>
        <div className="text-[#F7FAFC] text-2xl pg-heading mb-1.5" style={{ fontFamily: "'Rajdhani'" }}>
          Request an invite
        </div>
        <div className="text-[#9CA8B8] text-sm">Takes 30 seconds. We reply within a couple of days — approved partners get their link straight away.</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="pg-micro mb-1.5 block">// Full name <span className="text-[#A6FF00]">*</span></label>
          <input value={form.name} onChange={(e) => update("name", e.target.value)} required className={INPUT} data-testid="aff-apply-name" />
        </div>
        <div>
          <label className="pg-micro mb-1.5 block">// Email <span className="text-[#A6FF00]">*</span></label>
          <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className={INPUT} placeholder="you@lab.co.uk" data-testid="aff-apply-email" />
        </div>
      </div>
      <div>
        <label className="pg-micro mb-1.5 block">// Website / handle (optional)</label>
        <input value={form.website} onChange={(e) => update("website", e.target.value)} className={INPUT} placeholder="instagram.com/yourlab · yoursite.co.uk" data-testid="aff-apply-website" />
      </div>
      <div>
        <label className="pg-micro mb-1.5 block">// Where will you share? (optional)</label>
        <textarea rows={2} value={form.audience} onChange={(e) => update("audience", e.target.value)} className={INPUT} placeholder="e.g. a 400-member lab Discord, my newsletter, colleagues at two universities" data-testid="aff-apply-audience" />
      </div>
      <label className="flex items-start gap-3 text-[#9CA8B8] text-xs leading-relaxed cursor-pointer">
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 accent-[#A6FF00]" data-testid="aff-apply-accept" />
        <span>
          I'm 18+, I've read the{" "}
          <Link to="/legal/affiliate-terms" className="text-[#00C2D6] hover:underline">Affiliate Programme Terms</Link>{" "}
          and I understand these are research-use-only products that I must never promote for human consumption.
        </span>
      </label>
      <button type="submit" disabled={loading} className="pg-btn-primary w-full sm:w-auto text-sm" data-testid="aff-apply-submit">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Request my invite
      </button>
    </form>
  );
}
